import { ReactNode, Suspense, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CaretDown,
  CaretUp,
  Envelope,
  Globe,
  HashStraight,
  Info,
  MapPin,
  PencilSimple,
  Phone,
} from "@phosphor-icons/react"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { Button } from "@/components/ui/button"
import { ListItem, ListText } from "@/components/ui/list"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { joinStrings } from "@/lib/joinString"
import { selectTranslationNameForLanguage } from "@/lib/select-translation-name-for-language"
import { cn } from "@/lib/utils"
import { useAdvancedCareProviderSearchFiltersQuery$data } from "@/modules/care-provider/advanced-search/services/__generated__/useAdvancedCareProviderSearchFiltersQuery.graphql"
import { useAdvancedCareProviderSearchQuery$data } from "@/modules/care-provider/advanced-search/services/__generated__/useAdvancedCareProviderSearchQuery.graphql"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import { EditCareProviderDialog } from "@/modules/care-provider/components/edit-care-provider/edit-care-provider-dialog"
import { ScheduleDisplay } from "@/modules/care-provider/components/schedule-display"
import { careProviderSearchDoctorNetworksQuery$data } from "@/modules/care-provider/simple-search/__generated__/careProviderSearchDoctorNetworksQuery.graphql"
import { type ApplicableInsuranceCareProviderListIds } from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { combineConditionalCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { getCareProviderAddress } from "@/modules/care-provider/utils/get-care-provider-address"
import { getCareProviderIndicator } from "@/modules/care-provider/utils/get-care-provider-indicator"
import { getCareProviderRelationName } from "@/modules/care-provider/utils/get-care-provider-relation-name"
import { getHighlightValue } from "@/modules/care-provider/utils/get-highlight-value"

interface AdvancedCareProviderSearchItemProps {
  hasMPNReferral?: boolean
  applicableInsuranceCareProviderListIds: ApplicableInsuranceCareProviderListIds
  doctorNetworks?: NonNullable<careProviderSearchDoctorNetworksQuery$data["ch_patientById"]>["doctorNetworks"]
  careProvider: useAdvancedCareProviderSearchQuery$data["ch_careProviderAdvancedSearch"]["results"][number]
  officialLanguages: useAdvancedCareProviderSearchFiltersQuery$data["officialLanguages"]
  isAddable: boolean
  onCareProviderSelect: (relationId: string) => void
}

const AdvancedCareProviderSearchItem: React.FC<AdvancedCareProviderSearchItemProps> = ({
  hasMPNReferral,
  applicableInsuranceCareProviderListIds,
  careProvider,
  doctorNetworks,
  officialLanguages,
  isAddable,
  onCareProviderSelect,
}) => {
  const { t } = useTranslation()
  const currentLanguage = useCurrentLanguage()
  const [expanded, setExpanded] = useState(false)
  const [isOpenEditCareProviderDialog, setIsOpenEditCareProviderDialog] = useState(false)
  const doctorNetwork = useMemo(() => {
    return doctorNetworks?.find((x) => x.careProvider?.id === careProvider.id)
  }, [doctorNetworks, careProvider.id])

  const toggleExpanded = () => setExpanded(!expanded)

  if (!careProvider) {
    return null
  }

  const {
    id,
    phoneNumber,
    email,
    website,
    creditorCodeRegister,
    globalLocationNumber,
    careProviderGroupQuickList,
    careProviderSpecialtyTranslations,
    highlights,
    distance,
    groupName,
    isMedgatePartnerNetworkMember,
    isTakesNewPatients,
    officialLanguageIds,
    detailedSchedule,
    insuranceCareProviderListIds,
  } = careProvider

  const title = combineConditionalCareProviderTitle(careProvider, highlights)
  const { groupType } = careProviderGroupQuickList
  const address = getCareProviderAddress(careProvider)
  const formattedDistance = distance ? `${distance.toFixed(1).replace(/\.0+$/, "")} km` : ""
  const distanceAddress = joinStrings([formattedDistance, address])
  const specialtyNames = careProviderSpecialtyTranslations.map((item) => item.name)
  const [firstSpecialty] = specialtyNames
  const specialtiesAndGroup = joinStrings([firstSpecialty, groupName], ", ")
  const relation = getCareProviderRelationName(careProvider, t)

  const countryName = careProvider.country?.countryName
  const postalCity = joinStrings([careProvider.postalCode, careProvider.city], " ")
  const addressTitle = joinStrings([careProvider.streetAddress, postalCity, countryName], ", ")

  const formattedGlobalLocationNumber = globalLocationNumber
    ? t("admin.careProviders.advancedSearch.cardPopup.globalLocationNumber", {
        number: globalLocationNumber,
      })
    : null

  const formattedCreditorCodeRegister = creditorCodeRegister
    ? t("admin.careProviders.advancedSearch.cardPopup.zsrNumber", {
        number: creditorCodeRegister,
      })
    : null

  const numberResults = joinStrings([
    getHighlightValue(highlights, "creditorCodeRegister", formattedCreditorCodeRegister),
    formattedGlobalLocationNumber,
  ])

  const languages = joinStrings(
    officialLanguages
      .filter((item) => officialLanguageIds?.includes(item.officialLanguageLookupId))
      .map((item) => selectTranslationNameForLanguage(currentLanguage, item.translations)),
    ", ",
  )

  return (
    <>
      <div className="group" data-testid="care-provider-search-item">
        <div className="group/menuitem divide-border border-border grid items-center rounded-lg border">
          <div className="grid">
            <div className="grid-cols-1fr-auto grid content-center items-center rounded-lg px-3 py-0 text-left">
              <div className="grid py-5 pl-3">
                <div className="grid grid-cols-[2rem_minmax(0,2fr)_minmax(0,auto)] items-center gap-5">
                  <div>
                    <CareProviderIcon groupType={groupType} />
                  </div>
                  <div className="grid">
                    <PotentiallyUnsafeInnerHtml className="truncate" value={title} />
                    <div className="flex">
                      <PotentiallyUnsafeInnerHtml
                        className="text-on-surface-subdued truncate text-xs"
                        value={specialtiesAndGroup}
                      />
                      {specialtiesAndGroup && relation && (
                        <span className="text-on-surface-subdued text-xs">&nbsp;•&nbsp;</span>
                      )}
                      <PotentiallyUnsafeInnerHtml className="text-primary truncate text-xs" value={relation} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div>
                  {getCareProviderIndicator({
                    isMedgatePartnerNetworkMember: isMedgatePartnerNetworkMember,
                    isTakesNewPatients: isTakesNewPatients,
                    role: doctorNetwork?.role,
                    defaultMedgatePartnerNetworkAllowed: hasMPNReferral,
                    insuranceCareProviderListIds,
                    applicableInsuranceCareProviderListIds,
                  })}
                </div>
                <div className="flex items-center">
                  <div>
                    <Button onClick={toggleExpanded} className="flex h-10 w-10 items-center" variant="circle">
                      {expanded ? (
                        <CaretUp className="text-on-surface-subdued" />
                      ) : (
                        <CaretDown className="text-on-surface-subdued" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button className="h-10 w-10" variant="circle" onClick={() => setIsOpenEditCareProviderDialog(true)}>
                    <PencilSimple size={20} />
                  </Button>
                </div>
                <div className="flex items-center">
                  <div>
                    <Button
                      disabled={!isAddable}
                      onClick={() => {
                        FaroInsightsWrapper.pushEvent("care_provider_advanced_search_add")
                        onCareProviderSelect(id)
                      }}>
                      {t("common.add")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t"></div>
          </div>
          <div className="text-on-surface grid grid-cols-3 gap-2 p-3 px-5 text-sm">
            <div>
              {distanceAddress && (
                <CareProviderDetailItem
                  icon={<MapPin />}
                  value={distanceAddress}
                  title={joinStrings([formattedDistance, addressTitle])}
                />
              )}
              {expanded && (
                <>
                  {phoneNumber && <CareProviderDetailItem icon={<Phone />} value={phoneNumber} />}
                  {email && <CareProviderDetailItem icon={<Envelope />} value={email} />}
                  {website && <CareProviderDetailItem icon={<Globe />} value={website} />}
                  {numberResults && <CareProviderDetailItem icon={<HashStraight />} value={numberResults} />}
                </>
              )}
            </div>
            {detailedSchedule && detailedSchedule.length > 0 && (
              <ScheduleDisplay detailedSchedule={detailedSchedule} className="mx-2" isExpanded={expanded} />
            )}
            <div>
              {languages && (
                <ListItem className="flex h-9 items-center">
                  <Info className="text-on-surface-subdued shrink-0" />
                  <div className="ml-2 min-w-0">
                    {expanded && <ListText variant="secondary">{t("common.languages")}</ListText>}
                    <ListText className="text-on-surface truncate" title={languages}>
                      {languages}
                    </ListText>
                  </div>
                </ListItem>
              )}
              {expanded && specialtyNames.length > 0 && (
                <div className="mt-2 ml-10">
                  <span className="text-on-surface-subdued text-xs">
                    {t("admin.careProviders.advancedSearch.filter.specialty")}
                  </span>
                  <PotentiallyUnsafeInnerHtml
                    className="text-on-surface text-sm"
                    value={joinStrings(specialtyNames, ", <br />")}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isOpenEditCareProviderDialog && (
        <Suspense fallback={null}>
          <EditCareProviderDialog careProviderId={id} onClose={() => setIsOpenEditCareProviderDialog(false)} />
        </Suspense>
      )}
    </>
  )
}

interface CareProviderSectionLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  icon: ReactNode
}

const CareProviderDetailItem: React.FC<CareProviderSectionLayoutProps> = ({
  icon,
  value,
  className,
  title,
  ...rest
}) => {
  return (
    <div className={cn("text-on-surface flex items-center px-3 text-sm leading-tight", className)} {...rest}>
      <div className="text-on-surface-subdued py-1 pr-2">{icon}</div>
      <PotentiallyUnsafeInnerHtml title={title ?? value} className="truncate text-sm" value={value} />
    </div>
  )
}

export { AdvancedCareProviderSearchItem, CareProviderDetailItem }
